import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import * as Tooltip from '@radix-ui/react-tooltip';

import { selectAuthUser } from '../../../../redux/auth/auth.selectors';
import { updateUser } from '../../../../redux/auth/auth.actions';
import { selectCurrentWorkspace } from '../../../../redux/workspace/workspace.selectors';
import { toggleModalWorkspaceLeave } from '../../../../redux/main/main.actions';
import { getAvatarUrl } from '../../../../utils/get-avatar-url';

const formatDate = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const SettingsProfile = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const currentUser = useSelector(selectAuthUser);
    const workspace = useSelector(selectCurrentWorkspace);

    // Owners cannot leave (must transfer ownership or delete the workspace first).
    const isOwner = workspace?.my_role === 'owner' || workspace?.owner_id === currentUser?.id;

    const [name, setName] = useState(currentUser?.name || '');
    const [jobTitle, setJobTitle] = useState(currentUser?.jobTitle || '');
    const [username, setUsername] = useState(currentUser?.username || '');

    useEffect(() => {
        setName(currentUser?.name || '');
        setJobTitle(currentUser?.jobTitle || '');
        setUsername(currentUser?.username || '');
    }, [currentUser?.name, currentUser?.jobTitle, currentUser?.username]);

    // Save-on-blur: commit a single field when it loses focus, but only if its
    // trimmed value actually changed. Required fields revert to the persisted
    // value when blurred empty. The auth `updateUser` saga shows the notify.
    const commitField = (field, value, setter, { required = false } = {}) => {
        const trimmed = value.trim();
        const persisted = currentUser?.[field] || '';
        if (trimmed === persisted) return;
        if (required && !trimmed) {
            setter(persisted);
            return;
        }
        dispatch(updateUser({ [field]: trimmed }));
    };

    const avatarLetter = (currentUser?.name || currentUser?.email || '?')[0].toUpperCase();
    const memberSince = currentUser?.createdAt
        ? new Date(currentUser.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
        : '';

    return (
        <div className='settings-wrap'>
            <div className='settings-header'>{t('m_profile_menu.profile')}</div>

            {/* ── Hero card ─────────────────────────────────────── */}
            <div className='settings-hero'>
                <div className='settings-hero-avatar-wrap'>
                    {currentUser?.image
                        ? <img className='settings-hero-avatar-img' src={getAvatarUrl(currentUser.image, 88)} referrerPolicy="no-referrer" alt="" />
                        : <div className='settings-hero-avatar-placeholder'>{avatarLetter}</div>
                    }
                    <div className='settings-hero-status-dot' />
                </div>

                <div className='settings-hero-info'>
                    <div className='settings-hero-name'>{currentUser?.name || currentUser?.email || '—'}</div>
                    <div className='settings-hero-email'>{currentUser?.email || ''}</div>
                </div>
                {/* {memberSince && <span className='settings-hero-since'>Member since {memberSince}</span>} */}
                {memberSince && <span className='settings-hero-since'>{t('settings_page.profile.member_since', { date: formatDate(currentUser?.createdAt) })}</span>}
            </div>

            {/* ── General ──────────────────────────────────────── */}
            <div className='settings-subheader'>{t('settings_page.profile.general')}</div>
            <div className='settings-section settings-section-list'>
                <div className='settings-fields'>
                    <div className='settings-field-row'>
                        <span className='settings-field-label'>{t('settings_page.profile.email')}</span>
                        <div className='settings-field-input input-email settings-field-static'>
                            {currentUser?.email || ''}
                        </div>
                    </div>

                    <div className="settings-field-line"></div>

                    <div className='settings-field-row'>
                        <span className='settings-field-label'>{t('settings_page.profile.full_name')}</span>
                        <input
                            className='settings-field-input'
                            type='text'
                            value={name}
                            maxLength={80}
                            placeholder={t('settings_page.profile.full_name_placeholder')}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            onChange={(e) => setName(e.target.value)}
                            onBlur={() => commitField('name', name, setName, { required: true })}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        />
                    </div>

                    {/* <div className="settings-field-line"></div>

                    <div className='settings-field-row'>
                        <span className='settings-field-label'>Title</span>
                        <input
                            className='settings-field-input'
                            type='text'
                            value={jobTitle}
                            maxLength={80}
                            placeholder='software engineer'
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            onChange={(e) => setJobTitle(e.target.value)}
                            onBlur={() => commitField('jobTitle', jobTitle, setJobTitle)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        />
                    </div> */}

                    {/* <div className="settings-field-line"></div>

                    <div className='settings-field-row'>
                        <span className='settings-field-label'>Username</span>
                        <input
                            className='settings-field-input'
                            type='text'
                            value={username}
                            maxLength={80}
                            placeholder='username'
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            onChange={(e) => setUsername(e.target.value)}
                            onBlur={() => commitField('username', username, setUsername)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        />
                    </div> */}
                </div>

            </div>

            {/* ── Security ─────────────────────────────────────── */}
            <div className='settings-subheader'>{t('settings_page.profile.account_protection')}</div>
            {/* <div className='settings-subheader'>Security</div> */}
            <div className='settings-section'>
                <div className='settings-row'>
                    <div className='settings-row-left'>
                        <span className='settings-row-label'>{t('settings_page.profile.mfa_label')}</span>
                        <span className='settings-row-sub'>{t('settings_page.profile.mfa_sub')}</span>
                    </div>
                    <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                    <button className='settings-row-btn' disabled>
                        {t('settings_page.profile.mfa_enable')}
                    </button>
                    </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="bottom"
                                align="center"
                                sideOffset={8}
                            >
                                <span>{t('settings_page.profile.mfa_soon_tooltip')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </div>

            {/* ── Workspace access ─────────────────────────────── */}
            <div className='settings-subheader'>{t('settings_page.profile.workspace_access')}</div>
            <div className='settings-section'>
                <div className='settings-row'>
                    <div className='settings-row-left'>
                        <span className='settings-row-label'>{t('settings_page.profile.leave_workspace')}</span>
                        <span className='settings-row-sub'>{t('settings_page.profile.leave_workspace_sub')}</span>
                    </div>
                    {isOwner ? (
                        <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <button className='settings-row-btn btn-delete' disabled>
                                    {t('settings_page.profile.leave_workspace')}
                                </button>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls"
                                    side="bottom"
                                    align="center"
                                    sideOffset={8}
                                >
                                    {/* <span>You are the owner of this workspace</span> */}
                                    {/* <span>Owners can't leave workspaces, only delete</span> */}
                                    {/* <span>Owner should to transfer ownership before leave</span> */}
                                    {/* <span>Transfer workspace ownership before leaving</span> */}
                                    <span>{t('settings_page.profile.leave_owner_tooltip')}</span>
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    ) : (
                        <button
                            className='settings-row-btn btn-delete'
                            onClick={() => dispatch(toggleModalWorkspaceLeave(workspace))}
                            disabled={!workspace?.id}
                        >
                            {t('settings_page.profile.leave_workspace')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SettingsProfile;
